import { motion } from "framer-motion";
import {
	Circle,
	Facebook,
	Menu,
	Target
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import type { TabType } from "@/components/index/navbar-refund";

import { fetchDiscoveryProducts } from "@/api/discovery";
import DiscoveryFooter from "@/components/index/discovery-footer";
import NavbarRefund from "@/components/index/navbar-refund";
import { Button } from "@/components/ui/button";
import LoginPromptModalDiscovery from "@/components/ui/login-prompt-modal-discovery";
import { Skeleton } from "@/components/ui/skeleton";
import { stripCategorySuffix } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { createFileRoute, Link, useLocation, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/")({
	component: DiscoveryHome
});

export function Nav({ activeLink }: { activeLink: string }) {
	return (
		<nav className="hidden md:flex gap-4  bg-neutral-100 rounded-3xl p-1 border border-primary">
			<Button
				variant="link"
				className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "home" ? "text-primary underline" : ""}`}
				asChild
			>
				<Link
					to="/"
					className="text-sm"
				>
					Home
				</Link>
			</Button>

			<Button
				variant="link"
				className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "features" ? "text-primary underline" : ""}`}
				asChild
			>
				<Link
					to="/features"
					className="text-sm"
				>
					Features
				</Link>
			</Button>

			<Button
				variant="link"
				className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "about" ? "text-primary underline" : ""}`}
				asChild
			>
				<Link
					to="/about"
					className="text-sm"
				>
					About us
				</Link>
			</Button>
			<Button
				variant="link"
				className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "contact" ? "text-primary underline" : ""}`}
				asChild
			>
				<Link
					to="/contact"
					className="text-sm"
				>
					Contact us
				</Link>
			</Button>
			<Button
				variant="link"
				className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "legal" ? "text-primary underline" : ""}`}
				asChild
			>
				<Link
					to="/legal"
					className="text-sm"
				>
					Legal
				</Link>
			</Button>
			<Button className="rounded-full mr-2" asChild>
				<Link to="/" className="text-sm ">
					Get Started
				</Link>
			</Button>
		</nav>
	);
}

export function Header() {
	const location = useLocation();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const activeLink = location.pathname.split("/").pop() || "home";

	return (
		<motion.div
			initial={{ y: -20, opacity: 0 }}
			animate={{ y: 0, opacity: 1 }}
			transition={{ duration: 0.5, delay: 0.2 }}
			className="flex items-center justify-between gap-4 py-4 px-2"
		>
			<img
				src="/logo-with-color-black.png"
				className="w-48 max-md:w-32"
				alt="Platform Logo"
			/>
			<div className="mx-auto flex-1" />
			<Nav activeLink={activeLink} />

			{/* Mobile Menu Button */}
			<Button
				variant="ghost"
				className="md:hidden"
				onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
			>
				<Menu className="h-6 w-6" />
			</Button>

			{/* Mobile Menu */}
			<div className={`md:hidden	 transition-all duration-300 ease-in-out absolute top-16 left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50 ${isMobileMenuOpen ? "-translate-y-0" : "-translate-y-[400px]"}`}>
				<div className="flex flex-col p-4 space-y-2">
					<Button
						variant="link"
						className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "home" ? "text-primary underline" : ""}`}
						asChild
					>
						<Link
							to="/"
							className="text-sm"
							onClick={() => {
								setIsMobileMenuOpen(false);
							}}
						>
							Home
						</Link>
					</Button>

					<Button
						variant="link"
						className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "features" ? "text-primary underline" : ""}`}
						asChild
					>
						<Link
							to="/features"
							className="text-sm"
							onClick={() => {
								setIsMobileMenuOpen(false);
							}}
						>
							Features
						</Link>
					</Button>

					<Button
						variant="link"
						className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "about" ? "text-primary underline" : ""}`}
						asChild
					>
						<Link
							to="/about"
							className="text-sm"
							onClick={() => {
								setIsMobileMenuOpen(false);
							}}
						>
							About us
						</Link>
					</Button>

					<Button
						variant="link"
						className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "contact" ? "text-primary underline" : ""}`}
						asChild
					>
						<Link
							to="/contact"
							className="text-sm"
							onClick={() => {
								setIsMobileMenuOpen(false);
							}}
						>
							Contact us
						</Link>
					</Button>

					<Button
						variant="link"
						className={`text-black hover:text-primary transition-all duration-200 ${activeLink === "legal" ? "text-primary underline" : ""}`}
						asChild
					>
						<Link
							to="/legal"
							className="text-sm"
							onClick={() => {
								setIsMobileMenuOpen(false);
							}}
						>
							Legal
						</Link>
					</Button>

					<Button className="rounded-full" asChild>
						<Link to="/" className="text-sm">
							Get Started
						</Link>
					</Button>
				</div>
			</div>
		</motion.div>
	);
}

export function AppPromotionSection() {
	return (
		<section className="flex flex-wrap gap-4 min-h-96 my-8 bg-primary/90 rounded-3xl text-white">
			<div className="flex-1 px-8 p-10 max-md:p-4 max-md:px-4">
				<p className="text-4xl font-bold mt-4 max-w-xl max-md:text-2xl">
					Say Goodbye To Social Media Shopping Scams And Hello To Trusted
					Transactions.
				</p>
				<div className="flex gap-4 w-full max-md:items-center max-md:justify-center">
					<GooglePlayButton />
					<AppleButton />
				</div>
			</div>
			<div className="relative flex-1 flex items-center justify-center max-md:hidden">
				<img src="/products.png" alt="Products" className="max-w-lg " />
			</div>
		</section>
	);
}
function ProductCardSkeleton() {
	return (
		<div className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col">
			<Skeleton className="w-full aspect-[4/5]" />
			<div className="p-3 flex flex-col gap-2">
				<Skeleton className="h-3 w-20" />
				<Skeleton className="h-4 w-32" />
				<Skeleton className="h-4 w-16" />
			</div>
		</div>
	);
}

function DiscoveryHome() {
	const modalRef = useRef<HTMLDivElement>(null);
	const [showLoginPrompt, setShowLoginPrompt] = useState(false);
	useEffect(() => {
		if (showLoginPrompt && modalRef.current) {
			modalRef.current.focus();
		}
	}, [showLoginPrompt]);

	const lastProductCountRef = useRef(0);
	const [products, setProducts] = useState<any[]>([]);
	useEffect(() => {
		if (lastProductCountRef.current > 0 && products.length > lastProductCountRef.current) {
			const newProductIndex = lastProductCountRef.current;
			const productElements = document.querySelectorAll("[data-product-card]");
			if (productElements[newProductIndex]) {
				productElements[newProductIndex].scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}
	}, [products]);

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	const [activeTab, setActiveTab] = useState<TabType>("All" as unknown as TabType);
	const [activeCategoryId, setActiveCategoryId] = useState<string>("");

	const [search, setSearch] = useState("");
	const [limit] = useState(20);
	const [page, setPage] = useState(1);
	const [totalItems, setTotalItems] = useState(0);

	const categoryCacheRef = useRef<Record<string, { products: any[]; totalItems: number; page: number; scrollY: number }>>({});

	const cacheKey = "discovery-cache";
	const isRestoredRef = useRef(false);

	useEffect(() => {
		try {
			const cached = sessionStorage.getItem(cacheKey);
			if (cached) {
				const { products: cachedProducts, totalItems: cachedTotal, page: cachedPage, scrollY: cachedScrollY, search: cachedSearch, activeCategoryId: cachedCategory, activeTab: cachedTab } = JSON.parse(cached);
				setProducts(cachedProducts);
				setTotalItems(cachedTotal);
				setPage(cachedPage);
				setSearch(cachedSearch || "");
				setActiveCategoryId(cachedCategory || "");
				if (cachedTab)
					setActiveTab(cachedTab);
				setLoading(false);
				isRestoredRef.current = true;
				lastProductCountRef.current = 0;
				const categoryKey = cachedCategory || "all";
				categoryCacheRef.current[categoryKey] = { products: cachedProducts, totalItems: cachedTotal, page: cachedPage, scrollY: cachedScrollY || 0 };
				requestAnimationFrame(() => {
					window.scrollTo(0, cachedScrollY || 0);
				});
				sessionStorage.removeItem(cacheKey);
			}
		}
		catch {
			// Ignore cache errors
		}
	}, []);

	const fullCategoryDataRef = useRef<Record<string, { allProducts: any[]; totalItems: number }>>({});

	useEffect(() => {
		if (isRestoredRef.current) {
			isRestoredRef.current = false;
			return;
		}

		const categoryKey = activeCategoryId || "all";
		const cachedCategory = categoryCacheRef.current[categoryKey];

		if (cachedCategory && !search && page === 1) {
			setProducts(cachedCategory.products);
			setTotalItems(cachedCategory.totalItems);
			setLoading(false);
			if (cachedCategory.page > 1) {
				isRestoredRef.current = true;
				setPage(cachedCategory.page);
			}
			return;
		}

		const fullData = fullCategoryDataRef.current[categoryKey];
		if (fullData && !search && page >= 1) {
			const sliced = fullData.allProducts.slice(0, page * limit);
			setProducts(sliced);
			setTotalItems(fullData.totalItems);
			setLoading(false);
			categoryCacheRef.current[categoryKey] = { products: sliced, totalItems: fullData.totalItems, page, scrollY: window.scrollY };
			return;
		}

		setLoading(true);
		fetchDiscoveryProducts({
			limit,
			page,
			search,
			category: activeCategoryId
		})
			.then((data) => {
				let items: any[] = [];
				let total = 0;
				if (Array.isArray(data?.data?.items)) {
					items = data.data.items;
					total = data.data.meta?.totalItems || 0;
				}
				else if (Array.isArray(data?.items)) {
					items = data.items;
					total = data.meta?.totalItems || 0;
				}
				else if (Array.isArray(data)) {
					items = data;
					total = data.length;
				}

				if (page === 1 && items.length > limit) {
					const actualTotal = total || items.length;
					fullCategoryDataRef.current[categoryKey] = { allProducts: items, totalItems: actualTotal };
					const sliced = items.slice(0, limit);
					setProducts(sliced);
					setTotalItems(actualTotal);
					if (!search) {
						categoryCacheRef.current[categoryKey] = { products: sliced, totalItems: actualTotal, page: 1, scrollY: 0 };
					}
				}
				else if (page === 1) {
					setProducts(items);
					if (!search) {
						categoryCacheRef.current[categoryKey] = { products: items, totalItems: total, page: 1, scrollY: 0 };
					}
				}
				else {
					setProducts((prev) => {
						const existingIds = new Set(prev.map((p: any) => p.id));
						const newItems = items.filter((item: any) => !existingIds.has(item.id));
						const updatedProducts = [...prev, ...newItems];
						if (!search) {
							categoryCacheRef.current[categoryKey] = { products: updatedProducts, totalItems: total, page, scrollY: window.scrollY };
						}
						return updatedProducts;
					});
				}
				setTotalItems(total || items.length);
				setLoading(false);
			})
			.catch((err) => {
				setError("Failed to load products");
				setLoading(false);
				console.error("Discovery API error:", err);
			});
	}, [limit, page, search, activeCategoryId]);

	const saveDiscoveryState = () => {
		const currentKey = activeCategoryId || "all";
		if (categoryCacheRef.current[currentKey]) {
			categoryCacheRef.current[currentKey].scrollY = window.scrollY;
		}
		sessionStorage.setItem(cacheKey, JSON.stringify({
			products,
			totalItems,
			page,
			scrollY: window.scrollY,
			search,
			activeCategoryId,
			activeTab
		}));
	};

	const isAuthenticated = useAuthStore(state => state.isAuthenticated);

	const navigate = useNavigate();

	const handleProfileClick = (e: React.MouseEvent) => {
		e.preventDefault();
		saveDiscoveryState();
		if (!isAuthenticated) {
			setShowLoginPrompt(true);
		}
		else {
			const user = useAuthStore.getState().user;
			const userType = user?.userType?.toLowerCase() === "vendor" ? "vendor" : "buyer";
			navigate({ to: `/${userType}/home` });
		}
	};

	const handleBagClick = (e: React.MouseEvent) => {
		e.preventDefault();
		saveDiscoveryState();
		navigate({ to: "/bag" });
	};

	const handleLoginRedirect = () => {
		if (typeof window !== "undefined") {
			window.sessionStorage.setItem("afterLoginRedirect", "/buyer/home");
			window.location.href = "/sign-in";
		}
	};

	const [showScrollTop, setShowScrollTop] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			setShowScrollTop(window.scrollY > 300);
		};
		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const handleScrollToTop = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	return (
		<div className="bg-gray-50 min-h-screen flex flex-col">
			<NavbarRefund
				searchValue={search}
				onSearchChange={setSearch}
				onProfileClick={handleProfileClick}
				onBagClick={handleBagClick}
				activeTab={activeTab}
				setActiveTab={(tab: TabType) => {
					const currentKey = activeCategoryId || "all";
					if (categoryCacheRef.current[currentKey]) {
						categoryCacheRef.current[currentKey].scrollY = window.scrollY;
					}
					lastProductCountRef.current = 0;
					setActiveTab(tab);
					setActiveCategoryId(tab.id === "All" ? "" : tab.id);
					setPage(1);
					window.scrollTo(0, 0);
				}}
			/>
			<LoginPromptModalDiscovery
				open={showLoginPrompt}
				onClose={() => setShowLoginPrompt(false)}
				onLogin={handleLoginRedirect}
			/>

			<main className="flex-1 px-2 sm:px-4 md:px-8 py-6 md:py-8 w-full">
				{loading && page === 1
					? (
							<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
								{Array.from({ length: 8 }).map((_, idx) => (
									<ProductCardSkeleton key={idx} />
								))}
							</div>
						)
					: error
						? (
								<div className="text-center text-red-500">{error}</div>
							)
						: (
								<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
									{products.length === 0
										? (
												<div className="col-span-full text-center text-gray-400">No products found.</div>
											)
										: (
												<>
													{products.map((product: any, idx: number) => (
														<div
															key={product.id || idx}
															data-product-card
															className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col cursor-pointer hover:shadow-md transition-shadow"
															onClick={() => {
																saveDiscoveryState();
																navigate({ to: `/product/${product.id || idx}` });
															}}
															tabIndex={0}
															role="button"
															aria-label={`View details for ${stripCategorySuffix(product.name) || "Product"}`}
															onKeyPress={(e) => {
																if (e.key === "Enter") {
																	saveDiscoveryState();
																	navigate({ to: `/product/${product.id || idx}` });
																}
															}}
														>
															<div className="bg-gray-200 w-full aspect-[4/5] overflow-hidden flex items-center justify-center">
																<img
																	src={(product.photos && product.photos.length > 0) ? product.photos[0] : "/public/assets/icons/shoe1.jpg"}
																	alt={product.name}
																	loading="lazy"
																	className="w-full h-full object-cover"
																	style={{ display: "block" }}
																/>
															</div>
															<div className="p-3 flex flex-col gap-1">
																<span className="text-xs text-blue-700 font-semibold" aria-label="Vendor name">{product.vendorId?.fullName || "NAME OF STORE"}</span>
																<h2 className="text-sm text-gray-800 m-0" style={{ margin: 0 }}>{stripCategorySuffix(product.name) || "Product name"}</h2>
																<span className="text-sm font-bold text-gray-900">{product.price ? `₦${product.price}` : "₦--"}</span>
															</div>
														</div>
													))}
													{loading && page > 1 && (
														Array.from({ length: 8 }).map((_, idx) => (
															<ProductCardSkeleton key={`skeleton-${idx}`} />
														))
													)}
												</>
											)}
								</div>
							)}

				{products.length < totalItems && !loading && (
					<div className="flex flex-col items-center mt-8 gap-4">
						<span className="text-sm text-gray-700">
							Showing
							{" "}
							{products.length}
							{" "}
							of
							{" "}
							{totalItems}
							{" "}
							products
						</span>
						<Button
							onClick={() => {
								lastProductCountRef.current = products.length;
								setPage(prev => prev + 1);
							}}
							className="flex items-center gap-1.5 bg-[#3F2EC8] text-white px-3.5 py-3 rounded-full font-medium text-xs leading-[1.2] hover:bg-[#3426a8] transition-colors font-inter text-center"
						>
							See more
							<img src="/assets/icons/arrow-down.svg" alt="see more" className="flex items-center gap-2 w-5 h-5" />
						</Button>
					</div>
				)}

				{showScrollTop && (
					<Button
						onClick={handleScrollToTop}
						aria-label="Scroll to top"
						className="fixed bottom-20 right-4 z-50 bg-[#3F2EC8] text-white rounded-full shadow-lg w-12 h-12 flex items-center justify-center transition-opacity duration-300 md:bottom-40 md:right-10"
						style={{ paddingBottom: "env(safe-area-inset-bottom)", backgroundColor: "#3F2EC8" }}
						size="icon"
					>
						<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-chevron-up" aria-hidden="true"><polyline points="18 15 12 9 6 15"></polyline></svg>
					</Button>
				)}
			</main>
			<DiscoveryFooter />
		</div>
	);
}

export default function Features() {
	const features = [
		{
			title: "Secure Transactions",
			description:
        "End-to-end encryption and secure payment gateways to protect your financial data and ensure safe transactions.",
			bgColor: "bg-purple-100",
			iconBg: "bg-blue-600",
			icon: <Circle className="w-6 h-6 text-white fill-current" />,
			gridClass: "md:col-span-2 lg:col-span-2"
		},
		{
			title: "Secure Account",
			description:
        "Funds are held securely until contractual obligations and transaction are met.",
			bgColor: "bg-purple-200",
			iconBg: "bg-blue-600",
			icon: <Circle className="w-6 h-6 text-white fill-current" />,
			gridClass: "md:col-span-2"
		},
		{
			title: "Real Time Order Tracking",
			description:
        "Track your orders in real-time, including dispatch, transit, and delivery status directly from your buyer's dashboard",
			bgColor: "bg-white",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-1 md:row-span-1"
		},
		{
			title: "AI For Dispute Analysis",
			description:
        "Our AI feature automates the review process to speed up resolutions",
			bgColor: "bg-blue-700",
			iconBg: "bg-white",
			icon: <Circle className="w-6 h-6 text-blue-700 fill-current" />,
			textColor: "text-white",
			gridClass: "md:col-span-2 lg:col-span-2"
		},
		{
			title: "Integrated Logistics Network",
			description:
        "We integrated network of logistics agents to verify any product with contract terms before delivery to buyer.",
			bgColor: "bg-yellow-100",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-1"
		},
		{
			title: "Seamless Withdrawal",
			description:
        "Effortlessly withdraw your funds with easy steps directly to your bank mobile wallet within seconds.",
			bgColor: "bg-green-100",
			iconBg: "bg-purple-600",
			icon: <Target className="w-6 h-6 text-white" />,
			gridClass: "md:col-span-4"
		}
	];

	return (
		<motion.div
			initial={{ opacity: 0, y: 30 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			viewport={{ once: true }}
			className="p-6 max-w-7xl mx-auto max-md:p-0"
		>
			<section
				id="features"
				className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-fr my-6"
			>
				{features.map((feature, index) => (
					<motion.div
						key={feature.title}
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: index * 0.1 }}
						viewport={{ once: true }}
						className={`${feature.bgColor} ${feature.gridClass} rounded-2xl p-6 space-y-4 ${
							feature.textColor || "text-gray-900"
						} flex flex-col justify-between min-h-[200px]`}
					>
						<div className="space-y-4">
							<motion.div
								whileHover={{ scale: 1.1 }}
								transition={{ type: "spring", stiffness: 400, damping: 10 }}
								className={`${feature.iconBg} rounded-full w-12 h-12 flex items-center justify-center`}
							>
								{feature.icon}
							</motion.div>
							<div className="space-y-3">
								<h3 className="text-xl font-semibold leading-tight">
									{feature.title}
								</h3>
								<p
									className={`text-sm leading-relaxed ${feature.textColor ? "text-gray-200" : "text-gray-600"}`}
								>
									{feature.description}
								</p>
							</div>
						</div>
					</motion.div>
				))}
			</section>
		</motion.div>
	);
}

export function Footer() {
	return (
		<footer className="border-t bg-black border-gray-800 text-white">
			<div className=" text-white">
				{/* Call to Action Section */}
				<div className="px-6 py-16 text-center">
					<div className="mx-auto max-w-4xl">
						<h1 className="text-2xl font-bold mb-6 md:text-xl lg:text-3xl">
							Ready To Transform Your Vendor Relationship
						</h1>
						<p className="text-gray-300 text-sm mb-8 max-w-2xl mx-auto">
							Join thousands of businesses that have streamlined their procurement process and found reliable partners through our platform
						</p>
						<Button
							size="lg"
							className="text-white px-8 py-3 text-lg rounded-full"
							asChild
						>
							<Link to="/">Get Started For Free</Link>
						</Button>
					</div>
				</div>
			</div>
			<div className="px-6 py-12">
				<div className="mx-auto max-w-7xl">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
						{/* Company Info */}
						<div className="lg:col-span-1">
							<div className="flex items-center mb-4">
								<img
									src="/logo-with-color.png"
									className="w-48"
									alt="Platform Logo"
								/>
							</div>
							<p className="text-gray-400 text-sm leading-relaxed mb-4">
								Intasida is Nigeria's trusted e-commerce platform — connecting businesses with verified vendors for secure and
								reliable transactions. We safeguard your payments until you receive exactly what you ordered.
							</p>
							<div className="flex space-x-4 mt-4">
								<a
									href="https://www.facebook.com/share/17zkRkXbh2/"
									target="_blank"
									rel="noreferrer noopener"
									className="text-gray-400 hover:text-white transition-colors"
								>
									<Facebook className="size-5" />
								</a>
								<a
									href="https://www.tiktok.com/@intasida_official?_r=1&_t=ZS-94a0TknvQn7"
									target="_blank"
									rel="noreferrer noopener"
									className="text-gray-400 hover:text-white transition-colors"
								>
									<svg className="size-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.72a8.19 8.19 0 0 0 4.76 1.52V6.79a4.83 4.83 0 0 1-1-.1Z" /></svg>
								</a>
							</div>
						</div>

						{/* Quick Links */}
						<div>
							<h3 className="text-lg font-semibold mb-4">Quick Links</h3>
							<ul className="space-y-3">
								<li>
									<Link
										to="/"
										className="text-gray-400 hover:text-white transition-colors"
									>
										Home
									</Link>
								</li>
								<li>
									<Link
										to="/about"
										className="text-gray-400 hover:text-white transition-colors"
									>
										About Us
									</Link>
								</li>
								<li>
									<Link
										to="/features"
										className="text-gray-400 hover:text-white transition-colors"
									>
										Features
									</Link>
								</li>
								<li>
									<Link
										to="/contact"
										className="text-gray-400 hover:text-white transition-colors"
									>
										Contact Us
									</Link>
								</li>
							</ul>
						</div>

						{/* Services */}
						<div>
							<h3 className="text-lg font-semibold mb-4">Services</h3>
							<ul className="space-y-3 text-gray-400 text-sm">
								<li>Secure Payments</li>
								<li>Vendor Verification</li>
								<li>Order Tracking</li>
								<li>Dispute Resolution</li>
								<li>Integrated Logistics</li>
							</ul>
						</div>

						{/* Legal */}
						<div>
							<h3 className="text-lg font-semibold mb-4">Legal</h3>
							<ul className="space-y-3">
								<li>
									<Link
										to="/privacy"
										className="text-gray-400 hover:text-white transition-colors"
									>
										Privacy Policy
									</Link>
								</li>
								<li>
									<Link
										to="/terms"
										className="text-gray-400 hover:text-white transition-colors"
									>
										Terms of Service
									</Link>
								</li>
								<li>
									<Link
										to="/legal"
										className="text-gray-400 hover:text-white transition-colors"
									>
										Legal Information
									</Link>
								</li>
							</ul>
						</div>

						{/* Contact Us */}
						<div>
							<h3 className="text-lg font-semibold mb-4">Contact Us</h3>
							<div className="space-y-3 text-gray-400">
								<p className="flex items-center gap-2">
									<svg className="size-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
									info@intasida.com
								</p>
								<p className="flex items-center gap-2">
									<svg className="size-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
									markanthony@intasida.com
								</p>
								<p className="flex items-center gap-2">
									<svg className="size-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
									+234 814 222 4513
								</p>
								<p className="flex items-start gap-2">
									<svg className="size-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
										<path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
										<path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
									</svg>
									12 Mrs. Pelizer Ogar street, Independence layout, Enugu state.
								</p>
							</div>
						</div>
					</div>

					{/* Download Section */}
					<div className="mt-12 pt-8 border-t border-gray-800">
						<div className="flex flex-col md:flex-row items-center justify-between gap-6">
							<div>
								<h3 className="text-lg font-semibold mb-2">Download Intasida</h3>
								<p className="text-gray-400 text-sm">Get the app on your mobile device for a seamless experience.</p>
							</div>
							<div className="flex gap-4 items-center">
								<a
									href="https://play.google.com/store/apps/details?id=com.intasida&pcampaignid=web_share"
									target="_blank"
									rel="noreferrer noopener"
									className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg px-4 py-2 text-sm"
								>
									<svg className="size-5" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 01-.609-.92V2.734a.996.996 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 1.33-2.778 1.61-2.302-2.302 2.778-2.778v2.14zM5.864 2.658l10.937 6.333-2.302 2.302-8.635-8.635z" /></svg>
									Google Play
								</a>
								<a
									href="https://apps.apple.com/us/app/intasida/id6740424971"
									target="_blank"
									rel="noreferrer noopener"
									className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg px-4 py-2 text-sm"
								>
									<svg className="size-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
									App Store
								</a>
							</div>
						</div>
					</div>

					{/* Bottom Section */}
					<div className="flex flex-col md:flex-row justify-between items-center mt-12 pt-8 border-t border-gray-800">
						<p className="text-gray-400 text-sm mb-4 md:mb-0">
							© 2026 Intasida Limited. All rights reserved.
						</p>
						<div className="flex flex-wrap gap-4 text-gray-400 text-sm">
							<Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
							<Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
							<Link to="/contact" className="hover:text-white transition-colors">Support</Link>
							<a href="https://www.facebook.com/share/17zkRkXbh2/" target="_blank" rel="noreferrer noopener" className="hover:text-white transition-colors">
								<Facebook className="size-5" />
							</a>
							<a href="https://www.tiktok.com/@intasida_official?_r=1&_t=ZS-94a0TknvQn7" target="_blank" rel="noreferrer noopener" className="hover:text-white transition-colors">
								<svg className="size-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.72a8.19 8.19 0 0 0 4.76 1.52V6.79a4.83 4.83 0 0 1-1-.1Z" /></svg>
							</a>
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
}

export function GooglePlayButton() {
	return (
		<a
			target="_blank"
			rel="noreferrer noopener"
			href="https://play.google.com/store/apps/details?id=com.intasida&pcampaignid=web_share"
		>
			<svg className="size-[200px] max-md:size-[150px]" viewBox="0 -47.5 135 135" id="Layer_1" xmlns="http://www.w3.org/2000/svg">

				<defs>
					<style>
						{
							".st0{fill:#a6a6a6}.st1{stroke:#fff;stroke-width:.2;stroke-miterlimit:10}.st1,.st2{fill:#fff}.st3{fill:url(#SVGID_1_)}.st4{fill:url(#SVGID_2_)}.st5{fill:url(#SVGID_3_)}.st6{fill:url(#SVGID_4_)}.st7,.st8,.st9{opacity:.2;enable-background:new}.st8,.st9{opacity:.12}.st9{opacity:.25;fill:#fff}"
						}
					</style>
				</defs>

				<path d="M130 40H5c-2.8 0-5-2.2-5-5V5c0-2.8 2.2-5 5-5h125c2.8 0 5 2.2 5 5v30c0 2.8-2.2 5-5 5z" />

				<path className="st0" d="M130 .8c2.3 0 4.2 1.9 4.2 4.2v30c0 2.3-1.9 4.2-4.2 4.2H5C2.7 39.2.8 37.3.8 35V5C.8 2.7 2.7.8 5 .8h125m0-.8H5C2.2 0 0 2.3 0 5v30c0 2.8 2.2 5 5 5h125c2.8 0 5-2.2 5-5V5c0-2.7-2.2-5-5-5z" />

				<path className="st1" d="M47.4 10.2c0 .8-.2 1.5-.7 2-.6.6-1.3.9-2.2.9-.9 0-1.6-.3-2.2-.9-.6-.6-.9-1.3-.9-2.2 0-.9.3-1.6.9-2.2.6-.6 1.3-.9 2.2-.9.4 0 .8.1 1.2.3.4.2.7.4.9.7l-.5.5c-.4-.5-.9-.7-1.6-.7-.6 0-1.2.2-1.6.7-.5.4-.7 1-.7 1.7s.2 1.3.7 1.7c.5.4 1 .7 1.6.7.7 0 1.2-.2 1.7-.7.3-.3.5-.7.5-1.2h-2.2v-.8h2.9v.4zM52 7.7h-2.7v1.9h2.5v.7h-2.5v1.9H52v.8h-3.5V7H52v.7zM55.3 13h-.8V7.7h-1.7V7H57v.7h-1.7V13zM59.9 13V7h.8v6h-.8zM64.1 13h-.8V7.7h-1.7V7h4.1v.7H64V13zM73.6 12.2c-.6.6-1.3.9-2.2.9-.9 0-1.6-.3-2.2-.9-.6-.6-.9-1.3-.9-2.2s.3-1.6.9-2.2c.6-.6 1.3-.9 2.2-.9.9 0 1.6.3 2.2.9.6.6.9 1.3.9 2.2 0 .9-.3 1.6-.9 2.2zm-3.8-.5c.4.4 1 .7 1.6.7.6 0 1.2-.2 1.6-.7.4-.4.7-1 .7-1.7s-.2-1.3-.7-1.7c-.4-.4-1-.7-1.6-.7-.6 0-1.2.2-1.6.7-.4.4-.7 1-.7 1.7s.2 1.3.7 1.7zM75.6 13V7h.9l2.9 4.7V7h.8v6h-.8l-3.1-4.9V13h-.7z" />

				<path className="st2" d="M68.1 21.8c-2.4 0-4.3 1.8-4.3 4.3 0 2.4 1.9 4.3 4.3 4.3s4.3-1.8 4.3-4.3c0-2.6-1.9-4.3-4.3-4.3zm0 6.8c-1.3 0-2.4-1.1-2.4-2.6s1.1-2.6 2.4-2.6c1.3 0 2.4 1 2.4 2.6 0 1.5-1.1 2.6-2.4 2.6zm-9.3-6.8c-2.4 0-4.3 1.8-4.3 4.3 0 2.4 1.9 4.3 4.3 4.3s4.3-1.8 4.3-4.3c0-2.6-1.9-4.3-4.3-4.3zm0 6.8c-1.3 0-2.4-1.1-2.4-2.6s1.1-2.6 2.4-2.6c1.3 0 2.4 1 2.4 2.6 0 1.5-1.1 2.6-2.4 2.6zm-11.1-5.5v1.8H52c-.1 1-.5 1.8-1 2.3-.6.6-1.6 1.3-3.3 1.3-2.7 0-4.7-2.1-4.7-4.8s2.1-4.8 4.7-4.8c1.4 0 2.5.6 3.3 1.3l1.3-1.3c-1.1-1-2.5-1.8-4.5-1.8-3.6 0-6.7 3-6.7 6.6 0 3.6 3.1 6.6 6.7 6.6 2 0 3.4-.6 4.6-1.9 1.2-1.2 1.6-2.9 1.6-4.2 0-.4 0-.8-.1-1.1h-6.2zm45.4 1.4c-.4-1-1.4-2.7-3.6-2.7s-4 1.7-4 4.3c0 2.4 1.8 4.3 4.2 4.3 1.9 0 3.1-1.2 3.5-1.9l-1.4-1c-.5.7-1.1 1.2-2.1 1.2s-1.6-.4-2.1-1.3l5.7-2.4-.2-.5zm-5.8 1.4c0-1.6 1.3-2.5 2.2-2.5.7 0 1.4.4 1.6.9l-3.8 1.6zM82.6 30h1.9V17.5h-1.9V30zm-3-7.3c-.5-.5-1.3-1-2.3-1-2.1 0-4.1 1.9-4.1 4.3s1.9 4.2 4.1 4.2c1 0 1.8-.5 2.2-1h.1v.6c0 1.6-.9 2.5-2.3 2.5-1.1 0-1.9-.8-2.1-1.5l-1.6.7c.5 1.1 1.7 2.5 3.8 2.5 2.2 0 4-1.3 4-4.4V22h-1.8v.7zm-2.2 5.9c-1.3 0-2.4-1.1-2.4-2.6s1.1-2.6 2.4-2.6c1.3 0 2.3 1.1 2.3 2.6s-1 2.6-2.3 2.6zm24.4-11.1h-4.5V30h1.9v-4.7h2.6c2.1 0 4.1-1.5 4.1-3.9s-2-3.9-4.1-3.9zm.1 6h-2.7v-4.3h2.7c1.4 0 2.2 1.2 2.2 2.1-.1 1.1-.9 2.2-2.2 2.2zm11.5-1.8c-1.4 0-2.8.6-3.3 1.9l1.7.7c.4-.7 1-.9 1.7-.9 1 0 1.9.6 2 1.6v.1c-.3-.2-1.1-.5-1.9-.5-1.8 0-3.6 1-3.6 2.8 0 1.7 1.5 2.8 3.1 2.8 1.3 0 1.9-.6 2.4-1.2h.1v1h1.8v-4.8c-.2-2.2-1.9-3.5-4-3.5zm-.2 6.9c-.6 0-1.5-.3-1.5-1.1 0-1 1.1-1.3 2-1.3.8 0 1.2.2 1.7.4-.2 1.2-1.2 2-2.2 2zm10.5-6.6l-2.1 5.4h-.1l-2.2-5.4h-2l3.3 7.6-1.9 4.2h1.9l5.1-11.8h-2zm-16.8 8h1.9V17.5h-1.9V30z" />

				<g>

					<linearGradient id="SVGID_1_" gradientUnits="userSpaceOnUse" x1="21.8" y1="33.29" x2="5.017" y2="16.508" gradientTransform="matrix(1 0 0 -1 0 42)">

						<stop offset="0" stopColor="#00a0ff" />

						<stop offset=".007" stopColor="#00a1ff" />

						<stop offset=".26" stopColor="#00beff" />

						<stop offset=".512" stopColor="#00d2ff" />

						<stop offset=".76" stopColor="#00dfff" />

						<stop offset="1" stopColor="#00e3ff" />

					</linearGradient>

					<path className="st3" d="M10.4 7.5c-.3.3-.4.8-.4 1.4V31c0 .6.2 1.1.5 1.4l.1.1L23 20.1v-.2L10.4 7.5z" />

					<linearGradient id="SVGID_2_" gradientUnits="userSpaceOnUse" x1="33.834" y1="21.999" x2="9.637" y2="21.999" gradientTransform="matrix(1 0 0 -1 0 42)">

						<stop offset="0" stopColor="#ffe000" />

						<stop offset=".409" stopColor="#ffbd00" />

						<stop offset=".775" stopColor="orange" />

						<stop offset="1" stopColor="#ff9c00" />

					</linearGradient>

					<path className="st4" d="M27 24.3l-4.1-4.1V19.9l4.1-4.1.1.1 4.9 2.8c1.4.8 1.4 2.1 0 2.9l-5 2.7z" />

					<linearGradient id="SVGID_3_" gradientUnits="userSpaceOnUse" x1="24.827" y1="19.704" x2="2.069" y2="-3.054" gradientTransform="matrix(1 0 0 -1 0 42)">

						<stop offset="0" stopColor="#ff3a44" />

						<stop offset="1" stopColor="#c31162" />

					</linearGradient>

					<path className="st5" d="M27.1 24.2L22.9 20 10.4 32.5c.5.5 1.2.5 2.1.1l14.6-8.4" />

					<linearGradient id="SVGID_4_" gradientUnits="userSpaceOnUse" x1="7.297" y1="41.824" x2="17.46" y2="31.661" gradientTransform="matrix(1 0 0 -1 0 42)">

						<stop offset="0" stopColor="#32a071" />

						<stop offset=".069" stopColor="#2da771" />

						<stop offset=".476" stopColor="#15cf74" />

						<stop offset=".801" stopColor="#06e775" />

						<stop offset="1" stopColor="#00f076" />

					</linearGradient>

					<path className="st6" d="M27.1 15.8L12.5 7.5c-.9-.5-1.6-.4-2.1.1L22.9 20l4.2-4.2z" />

					<path className="st7" d="M27 24.1l-14.5 8.2c-.8.5-1.5.4-2 0l-.1.1.1.1c.5.4 1.2.5 2 0L27 24.1z" />

					<path className="st8" d="M10.4 32.3c-.3-.3-.4-.8-.4-1.4v.1c0 .6.2 1.1.5 1.4v-.1h-.1zM32 21.3l-5 2.8.1.1 4.9-2.8c.7-.4 1-.9 1-1.4 0 .5-.4.9-1 1.3z" />

					<path className="st9" d="M12.5 7.6L32 18.7c.6.4 1 .8 1 1.3 0-.5-.3-1-1-1.4L12.5 7.5c-1.4-.8-2.5-.2-2.5 1.4V9c0-1.5 1.1-2.2 2.5-1.4z" />

				</g>

			</svg>
		</a>

	);
}

export function AppleButton() {
	return (

		<a
			target="_blank"
			rel="noreferrer noopener"
			href="https://apps.apple.com/us/app/intasida/id6740424971" // Replace with your actual App Store ID
		>

			<svg className="size-[200px] max-md:size-[150px]" viewBox="0 -189.93 539.856 539.856" xmlns="http://www.w3.org/2000/svg">

				<g transform="scale(4.00216 4.0011)">

					<path fill="#FFF" d="M134.032 35.268a3.83 3.83 0 0 1-3.834 3.83H4.729a3.835 3.835 0 0 1-3.839-3.83V4.725A3.84 3.84 0 0 1 4.729.89h125.468a3.834 3.834 0 0 1 3.834 3.835l.001 30.543z" />

					<path fill="#A6A6A6" d="M130.198 39.989H4.729A4.73 4.73 0 0 1 0 35.268V4.726A4.733 4.733 0 0 1 4.729 0h125.468a4.735 4.735 0 0 1 4.729 4.726v30.542c.002 2.604-2.123 4.721-4.728 4.721z" />

					<path d="M134.032 35.268a3.83 3.83 0 0 1-3.834 3.83H4.729a3.835 3.835 0 0 1-3.839-3.83V4.725A3.84 3.84 0 0 1 4.729.89h125.468a3.834 3.834 0 0 1 3.834 3.835l.001 30.543z" />

					<path fill="#FFF" d="M30.128 19.784c-.029-3.223 2.639-4.791 2.761-4.864-1.511-2.203-3.853-2.504-4.676-2.528-1.967-.207-3.875 1.177-4.877 1.177-1.022 0-2.565-1.157-4.228-1.123-2.14.033-4.142 1.272-5.24 3.196-2.266 3.923-.576 9.688 1.595 12.859 1.086 1.554 2.355 3.287 4.016 3.226 1.625-.066 2.232-1.035 4.193-1.035 1.943 0 2.513 1.035 4.207.996 1.744-.027 2.842-1.56 3.89-3.127 1.255-1.779 1.759-3.533 1.779-3.623-.04-.014-3.386-1.292-3.42-5.154zM26.928 10.306c.874-1.093 1.472-2.58 1.306-4.089-1.265.056-2.847.875-3.758 1.944-.806.942-1.526 2.486-1.34 3.938 1.421.106 2.88-.717 3.792-1.793z" />

					<linearGradient id="a" gradientUnits="userSpaceOnUse" x1="-23.235" y1="97.431" x2="-23.235" y2="61.386" gradientTransform="matrix(4.0022 0 0 4.0011 191.95 -349.736)">

						<stop offset="0" stopColor="#1a1a1a" stopOpacity=".1" />

						<stop offset=".123" stopColor="#212121" stopOpacity=".151" />

						<stop offset=".308" stopColor="#353535" stopOpacity=".227" />

						<stop offset=".532" stopColor="#575757" stopOpacity=".318" />

						<stop offset=".783" stopColor="#858585" stopOpacity=".421" />

						<stop offset="1" stopColor="#b3b3b3" stopOpacity=".51" />

					</linearGradient>

					<path fill="url(#a)" d="M130.198 0H62.993l26.323 39.989h40.882a4.733 4.733 0 0 0 4.729-4.724V4.726A4.734 4.734 0 0 0 130.198 0z" />

					<g fill="#FFF">

						<path d="M53.665 31.504h-2.271l-1.244-3.909h-4.324l-1.185 3.909H42.43l4.285-13.308h2.646l4.304 13.308zm-3.89-5.549L48.65 22.48c-.119-.355-.343-1.191-.671-2.507h-.04c-.132.566-.343 1.402-.632 2.507l-1.106 3.475h3.574zM64.663 26.588c0 1.632-.443 2.922-1.33 3.869-.794.843-1.781 1.264-2.958 1.264-1.271 0-2.185-.453-2.74-1.361v5.035h-2.132V25.062c0-1.025-.027-2.076-.079-3.154h1.875l.119 1.521h.04c.711-1.146 1.79-1.719 3.238-1.719 1.132 0 2.077.447 2.833 1.342.755.897 1.134 2.075 1.134 3.536zm-2.172.078c0-.934-.21-1.704-.632-2.311-.461-.631-1.08-.947-1.856-.947-.526 0-1.004.176-1.431.523-.428.35-.708.807-.839 1.373a2.784 2.784 0 0 0-.099.649v1.601c0 .697.214 1.286.642 1.768.428.48.984.721 1.668.721.803 0 1.428-.311 1.875-.928.448-.619.672-1.435.672-2.449zM75.7 26.588c0 1.632-.443 2.922-1.33 3.869-.795.843-1.781 1.264-2.959 1.264-1.271 0-2.185-.453-2.74-1.361v5.035h-2.132V25.062c0-1.025-.027-2.076-.079-3.154h1.875l.119 1.521h.04c.71-1.146 1.789-1.719 3.238-1.719 1.131 0 2.076.447 2.834 1.342.754.897 1.134 2.075 1.134 3.536zm-2.173.078c0-.934-.211-1.704-.633-2.311-.461-.631-1.078-.947-1.854-.947-.526 0-1.004.176-1.433.523-.428.35-.707.807-.838 1.373-.065.264-.1.479-.1.649v1.601c0 .697.215 1.286.641 1.768.428.479.984.721 1.67.721.804 0 1.429-.311 1.875-.928.448-.619.672-1.435.672-2.449zM88.04 27.771c0 1.133-.396 2.054-1.183 2.765-.866.776-2.075 1.165-3.625 1.165-1.432 0-2.58-.276-3.446-.829l.493-1.777c.935.554 1.962.83 3.08.83.804 0 1.429-.182 1.875-.543.447-.362.673-.846.673-1.45 0-.541-.187-.994-.554-1.363-.369-.368-.979-.711-1.836-1.026-2.33-.869-3.496-2.14-3.496-3.812 0-1.092.412-1.986 1.234-2.685.822-.698 1.912-1.047 3.268-1.047 1.211 0 2.22.211 3.021.632l-.535 1.738c-.754-.408-1.605-.612-2.557-.612-.752 0-1.342.185-1.764.553-.355.329-.535.73-.535 1.206 0 .525.205.961.613 1.303.354.315 1 .658 1.934 1.026 1.146.462 1.988 1 2.527 1.618.543.618.813 1.389.813 2.308zM95.107 23.508h-2.35v4.659c0 1.185.414 1.776 1.244 1.776.381 0 .697-.032.947-.099l.059 1.619c-.42.157-.973.236-1.658.236-.842 0-1.5-.257-1.975-.771-.473-.514-.711-1.375-.711-2.587v-4.837h-1.4v-1.6h1.4v-1.757l2.094-.632v2.389h2.35v1.604zM105.689 26.627c0 1.475-.422 2.686-1.264 3.633-.881.975-2.053 1.461-3.514 1.461-1.41 0-2.531-.467-3.367-1.4-.836-.935-1.254-2.113-1.254-3.534 0-1.487.432-2.705 1.293-3.652.863-.948 2.025-1.422 3.486-1.422 1.408 0 2.539.468 3.395 1.402.818.906 1.225 2.076 1.225 3.512zm-2.21.049c0-.879-.19-1.633-.571-2.264-.447-.762-1.087-1.143-1.916-1.143-.854 0-1.509.381-1.955 1.143-.382.631-.572 1.398-.572 2.304 0 .88.19 1.636.572 2.265.461.762 1.104 1.143 1.937 1.143.815 0 1.454-.389 1.916-1.162.392-.646.589-1.405.589-2.286zM112.622 23.783a3.71 3.71 0 0 0-.672-.059c-.75 0-1.33.282-1.738.85-.354.5-.532 1.132-.532 1.895v5.035h-2.132V24.93a67.43 67.43 0 0 0-.062-3.021h1.857l.078 1.836h.059c.226-.631.58-1.14 1.066-1.521a2.578 2.578 0 0 1 1.541-.514c.197 0 .375.014.533.039l.002 2.034zM122.157 26.252a5 5 0 0 1-.078.967h-6.396c.024.948.334 1.674.928 2.174.539.446 1.236.67 2.092.67.947 0 1.811-.15 2.588-.453l.334 1.479c-.908.396-1.98.593-3.217.593-1.488 0-2.656-.438-3.506-1.312-.848-.875-1.273-2.051-1.273-3.524 0-1.446.395-2.651 1.186-3.612.828-1.026 1.947-1.539 3.355-1.539 1.383 0 2.43.513 3.141 1.539.563.813.846 1.821.846 3.018zm-2.033-.553c.015-.633-.125-1.178-.414-1.639-.369-.594-.937-.89-1.698-.89-.697 0-1.265.289-1.697.869-.355.461-.566 1.015-.631 1.658l4.44.002z" />

					</g>

					<g fill="#FFF">

						<path d="M45.211 13.491c-.593 0-1.106-.029-1.533-.078V6.979a11.606 11.606 0 0 1 1.805-.136c2.445 0 3.571 1.203 3.571 3.164 0 2.262-1.33 3.484-3.843 3.484zm.358-5.823c-.33 0-.611.02-.844.068v4.891c.126.02.368.029.708.029 1.602 0 2.514-.912 2.514-2.62 0-1.523-.825-2.368-2.378-2.368zM52.563 13.54c-1.378 0-2.271-1.029-2.271-2.426 0-1.456.912-2.494 2.349-2.494 1.358 0 2.271.98 2.271 2.417 0 1.474-.941 2.503-2.349 2.503zm.04-4.154c-.757 0-1.242.708-1.242 1.698 0 .971.495 1.679 1.232 1.679s1.232-.757 1.232-1.699c0-.96-.485-1.678-1.222-1.678zM62.77 8.717l-1.475 4.716h-.961l-.611-2.048a15.53 15.53 0 0 1-.379-1.523h-.02c-.077.514-.223 1.029-.378 1.523l-.65 2.048h-.971l-1.388-4.716h1.077l.534 2.242c.126.534.232 1.038.32 1.514h.02c.077-.397.203-.893.388-1.504l.67-2.251h.854l.641 2.203c.155.534.281 1.058.379 1.553h.028c.068-.485.175-1 .32-1.553l.573-2.203 1.029-.001zM68.2 13.433h-1.048v-2.708c0-.834-.32-1.252-.951-1.252-.621 0-1.048.534-1.048 1.155v2.805h-1.048v-3.368c0-.417-.01-.864-.039-1.349h.922l.049.728h.029c.282-.504.854-.824 1.495-.824.99 0 1.64.757 1.64 1.989l-.001 2.824zM71.09 13.433h-1.049v-6.88h1.049v6.88zM74.911 13.54c-1.377 0-2.271-1.029-2.271-2.426 0-1.456.912-2.494 2.348-2.494 1.359 0 2.271.98 2.271 2.417.001 1.474-.941 2.503-2.348 2.503zm.039-4.154c-.757 0-1.242.708-1.242 1.698 0 .971.496 1.679 1.231 1.679.738 0 1.232-.757 1.232-1.699.001-.96-.483-1.678-1.221-1.678zM81.391 13.433l-.076-.543h-.028c-.32.437-.787.65-1.379.65-.845 0-1.445-.592-1.445-1.388 0-1.164 1.009-1.766 2.756-1.766v-.087c0-.621-.329-.932-.979-.932-.465 0-.873.117-1.232.35l-.213-.689c.436-.272.98-.408 1.619-.408 1.232 0 1.854.65 1.854 1.951v1.737c0 .476.021.845.068 1.126l-.945-.001zm-.144-2.349c-1.164 0-1.748.282-1.748.951 0 .495.301.737.719.737.533 0 1.029-.407 1.029-.96v-.728zM87.357 13.433l-.049-.757h-.029c-.301.572-.807.864-1.514.864-1.137 0-1.979-1-1.979-2.407 0-1.475.873-2.514 2.065-2.514.631 0 1.078.213 1.33.641h.021V6.553h1.049v5.609c0 .456.011.883.039 1.271h-.933zm-.155-2.775c0-.66-.437-1.223-1.104-1.223-.777 0-1.252.689-1.252 1.659 0 .951.493 1.602 1.231 1.602.659 0 1.125-.573 1.125-1.252v-.786zM94.902 13.54c-1.377 0-2.27-1.029-2.27-2.426 0-1.456.912-2.494 2.348-2.494 1.359 0 2.271.98 2.271 2.417.001 1.474-.94 2.503-2.349 2.503zm.039-4.154c-.756 0-1.241.708-1.241 1.698 0 .971.495 1.679 1.231 1.679.738 0 1.232-.757 1.232-1.699.002-.96-.483-1.678-1.222-1.678zM102.887 13.433h-1.049v-2.708c0-.834-.32-1.252-.951-1.252-.621 0-1.047.534-1.047 1.155v2.805h-1.049v-3.368c0-.417-.01-.864-.039-1.349h.922l.049.728h.029c.281-.504.854-.825 1.494-.825.99 0 1.641.757 1.641 1.989v2.825zM109.938 9.503h-1.153v2.29c0 .583.202.874.61.874.185 0 .34-.02.465-.049l.029.796c-.203.078-.475.117-.813.117-.826 0-1.32-.456-1.32-1.65V9.503h-.688v-.786h.688v-.864l1.029-.311v1.174h1.153v.787zM115.486 13.433h-1.047v-2.688c0-.844-.319-1.271-.951-1.271-.543 0-1.049.369-1.049 1.116v2.843h-1.047v-6.88h1.047v2.833h.021c.33-.514.808-.767 1.418-.767.998 0 1.608.776 1.608 2.009v2.805zM121.17 11.327h-3.145c.02.893.611 1.397 1.486 1.397.465 0 .893-.078 1.271-.223l.163.728c-.446.194-.971.291-1.582.291-1.475 0-2.348-.932-2.348-2.377 0-1.446.894-2.533 2.23-2.533 1.205 0 1.961.893 1.961 2.242a2.02 2.02 0 0 1-.036.475zm-.961-.747c0-.728-.367-1.242-1.037-1.242-.602 0-1.078.524-1.146 1.242h2.183z" />

					</g>

				</g>

			</svg>
		</a>

	);
}
